import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Sort } from '@angular/material/sort';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter } from 'rxjs';
import { SubSink } from 'subsink';

import {
  ProductImportActions,
  ProductImportImagesPageRequest,
  ProductImportSelectors,
} from '@app/product-import/store/product-import.slice';
import { ErrorActions } from '@error/store/error.slice';
import { getImportTableColumns } from '@products/functions/table.functions';
import { IColumn } from '@products/interfaces/list-table.interface';
import { ProductImportImages } from '@products/interfaces/product-import-images';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { ListTableService } from '@shared/components/list-table/services/list-table.service';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';

@Component({
  selector: 'app-product-import-images',
  templateUrl: './product-import-images.component.html',
  styleUrls: ['./product-import-images.component.scss'],
})
export class ProductImportImagesComponent implements OnInit, OnDestroy {
  uploadForm = new FormGroup({
    file: new FormControl(''),
  });
  isLoading$ = this.store.select(
    ProductImportSelectors.selectProductImportImagesLoading
  );
  isUploading$ = this.store.select(
    ProductImportSelectors.selectProductImportImagesSaving
  );
  totalImages$ = this.store.select(
    ProductImportSelectors.selectTotalProductImportImages
  );
  displayedColumns: IColumn[] = [];
  productImagesTableSource = new MatTableDataSource<ProductImportImages>();
  selection = new SelectionModel<string>(true, []);

  private filter: ProductImportImagesPageRequest = {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  };

  private subs = new SubSink();
  private debounceTime: number = 500;

  readonly maxRequestSizeMiB = 512;
  private readonly maxRequestSize = this.maxRequestSizeMiB * 1024 * 1024;

  readonly maxFileSizeMiB = 3;
  private readonly maxFileSize = this.maxFileSizeMiB * 1024 * 1024;

  constructor(
    private readonly store: Store,
    private readonly listTableService: ListTableService,
    private readonly matDialog: MatDialog,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.displayedColumns = getImportTableColumns();
    this.createFilter(this.filter);
    this.subs.add(
      this.store
        .select(ProductImportSelectors.selectProductImportImagesList)
        .subscribe((images) => {
          if (images) {
            this.productImagesTableSource.data = images;
          }
        }),
      this.listTableService
        .getFilter()
        .pipe(debounceTime(this.debounceTime))
        .subscribe((filter: ProductImportImagesPageRequest) => {
          if (filter) {
            this.filter = filter;
            this.createFilter(this.filter);
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.listTableService.resetFilter();
    this.subs.unsubscribe();
  }

  onFileChange(event: any) {
    const files: File[] = Array.from(event.target.files);
    const fileSizeSum = files
      .map((file) => file.size)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    const largeFilenameList = files
      .filter((file) => file.size >= this.maxFileSize)
      .map((file) => file.name)
      .join(', ');

    const errorMessages: string[] = [];
    if (largeFilenameList) {
      errorMessages.push(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.FILE_SIZE', {
          size: this.maxFileSize / 1024 ** 2,
          unit: 'MiB',
          files: largeFilenameList,
        })
      );
    }
    if (fileSizeSum >= this.maxRequestSize) {
      errorMessages.push(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.REQUEST_SIZE', {
          size: this.maxRequestSize / 1024 ** 2,
          unit: 'MiB',
        })
      );
    }
    if (errorMessages.length > 0) {
      errorMessages.unshift(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.FILES_TOO_BIG')
      );
      event.target.value = '';
      return this.handleError(errorMessages.join('. '));
    }
    this.submitForm(structuredClone(event.target.files));
    event.target.value = '';
  }

  private handleError(messageOrNull?: string) {
    const message: string =
      messageOrNull ??
      this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.GENERIC');

    this.store.dispatch(
      ErrorActions.handleError({
        errorCode: '',
        globalErrors: {},
        fieldErrors: {},
        message: message,
        traceId: 0,
      })
    );
  }

  private submitForm(files?: FileList) {
    if (files) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('file', files[i]);
      }
      this.store.dispatch(
        ProductImportActions.saveProductImportImages.trigger({ formData })
      );
    }
  }

  openConfirmDialog(uploader: HTMLInputElement): void {
    const dialogRef: MatDialogRef<DialogComponent> = this.matDialog.open(
      DialogComponent,
      {
        data: {
          type: 'NOTICE',
          content: 'PRODUCTS.IMPORT.DIALOG.MSG',
        },
      }
    );

    dialogRef
      .afterClosed()
      .pipe(filter((result) => result))
      .subscribe(() => {
        uploader.click();
      });
  }

  public getCellValue(row: Record<string, string>, field: string): string {
    switch (field) {
      default:
        return row[field];
    }
  }

  public remove() {
    this.store.dispatch(
      ProductImportActions.deleteProductImportImage.trigger({
        fileNames: this.selection.selected,
      })
    );
  }

  public handleSortChanged(sortParams: Sort): void {
    this.createFilter(this.filter, sortParams);
  }

  private createFilter(
    filterOptions: ProductImportImagesPageRequest,
    sortParams?: Sort
  ): void {
    const queryParams: ProductImportImagesPageRequest = {
      ...(filterOptions ? filterOptions : {}),
      ...(sortParams?.active && sortParams?.direction
        ? {
            sort: [`${sortParams.active},${sortParams.direction}`],
          }
        : {}),
    } as ProductImportImagesPageRequest;
    this.fetchImages(queryParams);
  }

  private fetchImages(filter: ProductImportImagesPageRequest): void {
    this.store.dispatch(
      ProductImportActions.loadProductImportImages.trigger({ filter })
    );
  }
}
